import React from 'react';

import HpRespHeroModule from './HpRespHeroModule/HpRespHeroModule';
import { HpRespTopHeroBannerModule } from './HpRespTopHeroBannerModule/HpRespTopHeroBannerModule';
import { HpRespRecentlyViewedItemsLoader } from './HpRespRecentlyViewedItems/HpRespRecentlyViewedItemsLoader';
import { HpRespLocationsModule } from './HpRespLocationsModule/HpRespLocationsModule';
import { HpRespPersonalizedCategoriesLoader } from './HpRespPersonalizedCategories/HpRespPersonalizedCategoriesLoader';
import HpRespPromoModulePrimary from './HpRespPromoModule/HpRespPromoModulePrimary';
import HpRespPromoModuleSecondary from './HpRespPromoModule/HpRespPromoModuleSecondary';
import HpRespCollectionsModule from './HpRespCollectionsModule/HpRespCollectionsModule';
import { HpRespFeaturedModule } from './HpRespFeaturedModule/HpRespFeaturedModule';
import HpRespEditorialModule from './HpRespEditorialModule/HpRespEditorialModule';
import { HpRespShopByModule } from './HpRespShopByModule/HpRespShopByModule';
import HpRespSkinnyBannerModule from './HpRespSkinnyBannerModule/HpRespSkinnyBannerModule';
import HpRespCollectionSpotlightModule from './HpRespCollectionSpotlightModule/HpRespCollectionSpotlightModule';

import { HpRespLayout_viewer } from './__generated__/HpRespLayout_viewer.graphql';

type Homepage = NonNullable<HpRespLayout_viewer['homepage']>;
type HomepageModules = NonNullable<NonNullable<Homepage['modules']>[0]>;

type Props = {
    componentModule: HomepageModules;
    key: string;
    promoPrimaryItemsCount: number;
    viewer: HpRespLayout_viewer;
    isMobile: boolean;
    isClient: boolean;
    showLoggedInModules: boolean;
    itemsPerPage: number;
    personalization: Homepage['personalization'];
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
};

/* eslint-disable react/prop-types */
export const componentModuleMap: Record<string, (payload: Props) => JSX.Element | null> = {
    skinnyBanner: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules }) => (
        <HpRespSkinnyBannerModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    topHeroBanner: ({
        componentModule,
        key,
        useLazyLoadImages,
        isMobile,
        moduleIndex,
        totalModules,
    }) => (
        <HpRespTopHeroBannerModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    hero: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules }) => (
        <HpRespHeroModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    collections: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules }) => (
        <HpRespCollectionsModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    promoPrimary: ({
        componentModule,
        key,
        useLazyLoadImages,
        isMobile,
        moduleIndex,
        totalModules,
    }) => (
        <HpRespPromoModulePrimary
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    promoSecondary: ({
        componentModule,
        promoPrimaryItemsCount,
        key,
        useLazyLoadImages,
        isMobile,
        moduleIndex,
        totalModules,
    }) => (
        <HpRespPromoModuleSecondary
            key={key}
            componentModule={componentModule}
            promoPrimaryItemsCount={promoPrimaryItemsCount}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    featured: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules }) => (
        <HpRespFeaturedModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    editorial: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules }) => (
        <HpRespEditorialModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    shopBy: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules }) => (
        <HpRespShopByModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    locations: ({
        componentModule,
        viewer,
        key,
        itemsPerPage,
        isMobile,
        useLazyLoadImages,
        moduleIndex,
        totalModules,
    }) => (
        <HpRespLocationsModule
            key={key}
            componentModule={componentModule}
            viewer={viewer || null}
            itemsPerPage={itemsPerPage}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    recentlyViewed: ({
        viewer,
        isClient,
        isMobile,
        key,
        itemsPerPage,
        componentModule,
        moduleIndex,
        totalModules,
    }) =>
        isClient ? (
            <HpRespRecentlyViewedItemsLoader
                key={key}
                user={viewer?.user || null}
                itemsPerPage={itemsPerPage}
                showFullHeader
                additionalSpacing={isMobile}
                componentModule={componentModule}
                moduleIndex={moduleIndex}
                totalModules={totalModules}
            />
        ) : null,
    arrivals: ({
        isClient,
        isMobile,
        showLoggedInModules,
        personalization,
        viewer,
        componentModule,
        key,
        moduleIndex,
        totalModules,
    }) =>
        isClient && showLoggedInModules ? (
            <HpRespPersonalizedCategoriesLoader
                key={key}
                personalization={personalization}
                viewer={viewer}
                hasBorder={!isMobile}
                hasNoPageGutter={isMobile}
                pageSetOffset={!isMobile}
                componentModule={componentModule}
                moduleIndex={moduleIndex}
                totalModules={totalModules}
            />
        ) : null,
    collectionSpotlight: ({
        componentModule,
        key,
        isMobile,
        useLazyLoadImages,
        moduleIndex,
        totalModules,
    }) => (
        <HpRespCollectionSpotlightModule
            key={key}
            componentModule={componentModule}
            isMobile={isMobile}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
};
